import type { UserSessionSummary } from "@repo/shared/types";
import { enGB, nb } from "date-fns/locale";
import { Laptop, Monitor, Smartphone, Tablet } from "lucide-react";
import { useEffect, useState, useTransition } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { getUserSessions } from "@/lib/api/security";
import { authClient } from "@/lib/auth-client";
import { useUntrustDevice } from "@/lib/hooks/use-security";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { log } from "@/lib/logger/browser-logger";
import { distanceToNowFromDb } from "@/lib/utils";
import { T } from "./session-management.i18n";

type SessionManagementProps = {
  currentSessionId: string;
  isTrusted: boolean;
  twoFactorEnabled: boolean;
};

type Session = UserSessionSummary;

function getDeviceIcon(userAgent: string | null) {
  if (!userAgent) return <Monitor className="h-5 w-5" />;

  const ua = userAgent.toLowerCase();
  if (ua.includes("mobile") || ua.includes("iphone") || ua.includes("android")) {
    if (ua.includes("tablet") || ua.includes("ipad")) {
      return <Tablet className="h-5 w-5" />;
    }
    return <Smartphone className="h-5 w-5" />;
  }
  return <Laptop className="h-5 w-5" />;
}

function getDeviceName(
  userAgent: string | null,
  unknownDevice: string,
  unknownBrowser: string,
): string {
  if (!userAgent) return unknownDevice;

  const ua = userAgent.toLowerCase();

  // Detect browser
  let browser = unknownBrowser;
  if (ua.includes("firefox")) browser = "Firefox";
  else if (ua.includes("edg/")) browser = "Edge";
  else if (ua.includes("chrome")) browser = "Chrome";
  else if (ua.includes("safari")) browser = "Safari";

  // Detect OS
  let os = "";
  if (ua.includes("windows")) os = "Windows";
  else if (ua.includes("mac")) os = "macOS";
  else if (ua.includes("linux")) os = "Linux";
  else if (ua.includes("iphone")) os = "iPhone";
  else if (ua.includes("ipad")) os = "iPad";
  else if (ua.includes("android")) os = "Android";

  return os ? `${browser} on ${os}` : browser;
}

export function SessionManagement({
  currentSessionId,
  isTrusted: initialIsTrusted,
  twoFactorEnabled,
}: SessionManagementProps) {
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const [isPending, startTransition] = useTransition();
  const untrustMutation = useUntrustDevice();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isTrusted, setIsTrusted] = useState(initialIsTrusted);

  // Get date-fns locale based on user language
  const dateLocale = userLanguage === "nb_NO" ? { locale: nb } : { locale: enGB };

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    setLoading(true);
    setError(null);
    try {
      setSessions(await getUserSessions());
    } catch (err) {
      log.error({ err }, "failed to load sessions");
      setError(t.failedToLoadSessions);
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeOtherSessions = () => {
    setError(null);
    setSuccess(null);

    startTransition(async () => {
      const result = await authClient.revokeOtherSessions();
      if (result.error) {
        setError(result.error.message || t.failedToRevokeSessions);
        return;
      }

      setSuccess(t.allOtherDevicesLoggedOut);
      loadSessions();
    });
  };

  const handleUntrustDevice = () => {
    setError(null);
    setSuccess(null);

    untrustMutation.mutate(undefined, {
      onSuccess: () => {
        setIsTrusted(false);
        setSuccess(t.untrustDeviceSuccess);
      },
      onError: () => {
        setError(t.failedToUntrustDevice);
      },
    });
  };

  const otherSessions = sessions.filter((s) => s.id !== currentSessionId);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.activeSessions}</CardTitle>
        <CardDescription>{t.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">{error}</div>
        )}
        {success && (
          <div className="text-sm rounded-md border border-success/40 bg-success/10 p-3">
            {success}
          </div>
        )}

        {loading ? (
          <p className="text-sm text-muted-foreground">{t.loadingSessions}</p>
        ) : (
          <div className="space-y-3">
            {sessions.map((session) => {
              const isCurrent = session.id === currentSessionId;
              return (
                <div
                  key={session.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="text-muted-foreground">{getDeviceIcon(session.userAgent)}</div>
                    <div>
                      <div className="font-medium flex items-center gap-2">
                        {getDeviceName(session.userAgent, t.unknownDevice, t.unknownBrowser)}
                        {isCurrent && (
                          <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                            {t.current}
                          </span>
                        )}
                        {isCurrent && isTrusted && twoFactorEnabled && (
                          <span className="text-xs bg-success/10 text-success px-2 py-0.5 rounded">
                            {t.trusted}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {session.ipAddress || t.unknownIp} •{" "}
                        {distanceToNowFromDb(session.createdAt, {
                          addSuffix: true,
                          ...dateLocale,
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleRevokeOtherSessions}
            disabled={isPending || otherSessions.length === 0}
          >
            {t.logOutOtherDevices}
          </Button>
          {twoFactorEnabled && isTrusted && (
            <Button
              variant="outline"
              onClick={handleUntrustDevice}
              disabled={isPending || untrustMutation.isPending}
            >
              {t.untrustThisDevice}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
